import React, { useEffect, useState, useCallback } from 'react';
import { Users2, RefreshCw, MapPin, AlertCircle } from 'lucide-react';

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any }> = ({ children, className = '', title, icon: Icon }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
    {(title || Icon) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3 bg-white/[0.02]">
        {Icon && <Icon className="w-5 h-5 text-blue-400" />}
        {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase">{title}</h3>}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

interface DensityEvent {
  id: string;
  camera: string;
  location: string;
  message: string;
  confidence: number;
  imageUrl?: string;
  timestamp: Date;
}

export function PeopleCount() {
  const [events, setEvents] = useState<DensityEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/alerts?usecase=crowd-detection&limit=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const mapped: DensityEvent[] = (data.alerts || []).map((a: any) => ({
          id: a.id,
          camera: a.cameraName || 'Unknown Camera',
          location: a.location || 'Unknown',
          message: a.message || 'Crowd density threshold exceeded',
          confidence: Math.round((parseFloat(a.confidence) || 0) * (a.confidence <= 1 ? 100 : 1)),
          imageUrl: a.image_url || undefined,
          timestamp: a.timestamp ? new Date(a.timestamp) : new Date()
        }));
        setEvents(mapped);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      <div>
        <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-3"><Users2 className="w-8 h-8 text-blue-400" /> People Count</h1>
        <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Crowd-density alert events from cameras running Crowd Detection.</p>
      </div>

      <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4">
        <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          The current AI model raises an event when a density <strong>threshold</strong> is crossed, not a continuous live headcount. A dedicated people-counting model is on the roadmap for a running occupancy number.
        </p>
      </div>

      <GlassPanel><p className="text-[10px] uppercase tracking-widest text-blue-100/40 font-bold mb-1">Density Events Logged</p><h4 className="text-2xl font-black text-white">{events.length}</h4></GlassPanel>

      <GlassPanel className="p-0" title="Crowd Density Events" icon={Users2}>
        <div className="flex justify-end mb-4">
          <button onClick={fetchEvents} className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-blue-100/60 hover:text-white flex items-center gap-2 text-xs uppercase font-bold">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>

        {loading ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : events.length === 0 ? (
          <div className="py-20 text-center text-blue-100/20 text-sm uppercase tracking-widest">No crowd density events yet. Assign "Crowd Detection" to a camera to start.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {events.map(e => (
              <div key={e.id} className="flex gap-4 bg-white/5 border border-white/10 rounded-2xl p-4">
                <div className="w-28 h-20 rounded-xl bg-black/40 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {e.imageUrl ? <img src={e.imageUrl} className="w-full h-full object-cover" /> : <Users2 className="w-6 h-6 text-blue-100/10" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-blue-100/70 leading-snug">{e.message}</p>
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-blue-100/40 uppercase font-bold">
                    <span>{e.camera}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{e.location}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-blue-100/20 font-mono">{e.timestamp.toLocaleString()}</span>
                    <span className="text-[10px] text-emerald-400 font-bold">{e.confidence}% confidence</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
